import { ChangeDetectionStrategy, Component, Input, OnDestroy, ViewChild } from '@angular/core';
import { filter, take } from 'rxjs/operators';
import { QueryContactCenter, EQueryStageData } from '../models/e-query-stage';
import { BehaviorSubject, Subscription } from 'rxjs';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { EscalationMailFormControl } from '../escalations/escalations.component';
import { FileFormArray } from '../../../covance-file-upload/file-form-array';
import { UrlBuilder } from '../../../core';
import { maxLengthInBytes } from '../../../shared/validators';
import { EQueryCreateData } from '../models/e-query-create-data';
import { IMdlCustomDialogConfiguration, MdlDialogService } from '@angular-mdl/core';
import { ACCESSION_SUBJECT_TOKEN, AdminEQueryPreviewDialogComponent, EQUERY_EDIT_TOKEN } from '../e-query-preview/admin-e-query-preview-dialog.component';
import { EQueryQuestionCreateData } from '../models/e-query-create-data';
import { EQueryQuestionInfo } from '../e-query-details/questions/e-query-question-info';
import { EQueryAttachmentsUploaderComponent } from './e-query-attachments-uploader/e-query-attachments-uploader.component';
import { UserStorage } from '../../../user';
export class PrefilledDataError {
    constructor(public readonly error: string) {}
}

@Component({
    selector: 'admin-prefilled-e-query-edit',
    template: require('./admin-prefilled-e-query-edit.component.html'),
    styles: [require('./admin-prefilled-e-query-edit.component.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdminPrefilledEQueryEditComponent implements OnDestroy {
    @Input()
    public set prefilledData(value: EQueryStageData) {
        this.prefilledData$.next(value);
    }

    @ViewChild('attachmentsUploader')
    public attachmentsUploader: EQueryAttachmentsUploaderComponent;

    public readonly prefilledData$ = new BehaviorSubject<EQueryStageData>(undefined);
    public readonly editorForm: FormGroup;
    public readonly escalationMailsSiteCtrl = new EscalationMailFormControl();
    public readonly escalationMails2Ctrl = new EscalationMailFormControl();
    public readonly escalationMails1Ctrl = new EscalationMailFormControl();
    public readonly fileFormArray = new FileFormArray(false);
    public readonly eQueryTextCtrl: FormControl;

    private readonly textMaxBytes = 2050;
    private readonly subscription: Subscription;

    constructor(
        private readonly dialogService: MdlDialogService,
        private readonly urlBuilder: UrlBuilder,
        private readonly userStorage: UserStorage,
    ) {
        this.escalationMailsSiteCtrl = new FormControl([], [Validators.required]);
        this.escalationMails1Ctrl = new FormControl([]);
        this.escalationMails2Ctrl = new FormControl([]);
        this.eQueryTextCtrl = new FormControl('', [maxLengthInBytes(this.textMaxBytes)]);
        this.editorForm = new FormGroup(
            {
                'escalationMailsSite': this.escalationMailsSiteCtrl,
                'escalationMails1': this.escalationMails1Ctrl,
                'escalationMails2': this.escalationMails2Ctrl,
                'comments': this.eQueryTextCtrl,
                'files': this.fileFormArray
            },
        );
        this.subscription = this.prefilledData$.pipe(
            filter(v => !!v)
        ).subscribe(v => {
            this.escalationMailsSiteCtrl.setValue(v.escalations ? v.escalations.map(escalation => escalation.emailAddress) : []);
        });
    }

    public async save() {
        const prefilledData = await this.prefilledData$.pipe(
            filter(v => !this.isPrefilledDataInvalid(v)),
            take(1)
        ).toPromise();
        const configDialog: IMdlCustomDialogConfiguration = {
            component: AdminEQueryPreviewDialogComponent,
            providers: [{
                provide: EQUERY_EDIT_TOKEN, useValue: this.getEQueryData(prefilledData)
            },
            {
                provide: ACCESSION_SUBJECT_TOKEN, useValue: prefilledData.accessionSubject
            }],
            isModal: true,
            clickOutsideToClose: true,
            classes: 'e-query-preview-dialog'
        };

        const dialogRef = await this.dialogService.showCustomDialog(configDialog).toPromise();
        const isSubmitted = await dialogRef.onHide().toPromise<boolean>();
        if (isSubmitted) {
            await this.navigateToSummaryTab();
        }
    }

    public async cancel() {
        try {
            await this.dialogService.confirm(`Do you want to cancel this eQuery?`, 'No', 'Yes').toPromise();
            await this.navigateToSummaryTab();
            return true;
        } catch {
            return false;
        }
    }

    public mapPrefilledQuestionsToQuestionsInfos(prefilledQuestions: QueryContactCenter[])
        : EQueryQuestionInfo[] {
        return prefilledQuestions.map<EQueryQuestionInfo>(pq => {
           return {
               queryText: pq.query,
               issueTypeDescription: pq.issueDesc,
               issueType: pq.issueType
           };
        });
    }

    public isPrefilledDataInvalid<T>(obj: T | PrefilledDataError): obj is PrefilledDataError {
        return obj && obj instanceof PrefilledDataError;
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    private async getEQueryUrlPart() {
        const currentUser = await this.userStorage.currentUser.toPromise();
        if (currentUser.isAdmin) {
            return this.urlBuilder
                .esa()
                .admin()
                .adminEQuery();
        }

        if (currentUser.isInternal) {
            return this.urlBuilder
                .esa()
                .admin()
                .coordinatorEQuery();
        }

        throw new Error('Access denied.');
    }

    private async navigateToSummaryTab() {
        const urlPart = await this.getEQueryUrlPart();
        await urlPart
            .eQuerySummary()
            .navigate();
    }

    private getEQueryData(prefilledEQueryCreate: EQueryStageData): EQueryCreateData {
        const files = this.attachmentsUploader.getFilesWithDescriptions();

        const eQueryData: EQueryCreateData = {
            projectId: prefilledEQueryCreate.projectId,
            commentText: this.eQueryTextCtrl.value,
            accessionId: prefilledEQueryCreate.accessionSubject.accessionId,
            escalationMails1: this.escalationMails1Ctrl.value.filter(data => {
                return data !== '';
            }),
            escalationMails2: this.escalationMails2Ctrl.value.filter(data => {
                return data !== '';
            }),
            escalationMailsSite: this.escalationMailsSiteCtrl.value.filter(data => {
                return data !== '';
            }),
            files: files,
            questions: prefilledEQueryCreate.queries.map<EQueryQuestionCreateData>(q => {
                return  {
                    issueType: q.issueType,
                    issueDescription: q.issueDesc,
                    queryText: q.query,
                    responseMask: q.mask,
                    responseMaskDescription: q.maskDesc
                };
            })
        };

        return eQueryData;
    }
}
